/**
 * The brand, drawn once. Every screen that shows the app's logo or name as an
 * identity — the sidebar, the sign-in and sign-up pages, anything added later —
 * renders one of these, so changing design/brand.json changes all of them.
 *
 * Why this exists: the sidebar and the sign-in page each drew their own logo.
 * When the sidebar learned to read `brand.mark`, the sign-in page kept the
 * placeholder, and the first project shipped with two different logos.
 * `brand.test.ts` now fails if any other file reads `brand.mark`/`brand.wordmark` or draws the
 * placeholder, so a second copy cannot creep back in (DESIGN.md, "Where the
 * brand appears").
 */
import { Sparkles } from 'lucide-react'
import { brand, markFor } from '@/lib/brand'
import { cn } from '@/lib/utils'

const MARK_SIZES = {
  sm: { box: 'size-8', icon: 'size-4' },
  md: { box: 'size-9', icon: 'size-5' },
} as const

type MarkSize = keyof typeof MARK_SIZES

/** The mark alone: the brand's image, or a neutral placeholder until it has one. */
export function BrandMark({
  size = 'sm',
  tone = 'surface',
  className,
}: {
  size?: MarkSize
  tone?: 'sidebar' | 'surface'
  className?: string
}) {
  const s = MARK_SIZES[size]
  const mark = markFor(tone)
  if (mark) {
    return (
      <img src={mark} alt="" className={cn(s.box, 'shrink-0 rounded-lg object-cover', className)} />
    )
  }
  return (
    <span
      className={cn(
        s.box,
        'flex shrink-0 items-center justify-center rounded-lg bg-primary text-primary-foreground',
        className,
      )}
    >
      <Sparkles className={s.icon} aria-hidden="true" />
    </span>
  )
}

/**
 * The mark with the name beside it. `tone` picks text colours for the surface
 * underneath: `sidebar` uses the sidebar's own foreground, which stays readable
 * on any sidebar colour a brand chooses; `surface` is for the page background.
 */
export function BrandLockup({
  tone,
  size = 'sm',
  showWorkspace = false,
}: {
  tone: 'sidebar' | 'surface'
  size?: MarkSize
  /** The sidebar's second line when there is no eyebrow. */
  showWorkspace?: boolean
}) {
  if (brand.wordmark) {
    // The wordmark already holds the eyebrow and name at their exact
    // proportions — draw it as one piece, never re-typeset it.
    const label = brand.eyebrow ? `${brand.eyebrow} ${brand.shortName}` : brand.name
    // The mark is exactly as tall as the two lines of the wordmark, as in a
    // printed lockup. A brand sets that height with --brand-lockup-height
    // (default 2.5rem) to suit its sidebar width. In the collapsed icon rail
    // only the mark shows.
    //
    // On the sidebar, hovering the logo turns its button the light hover
    // colour — so the light-on-dark artwork would wash out. On hover it swaps
    // to the brand's light-surface versions (dark text, coloured tile).
    const swap = tone === 'sidebar'
    const markSize = cn(
      size === 'md' ? 'size-12' : 'size-[var(--brand-lockup-height,2.5rem)]',
      'group-data-[collapsible=icon]:size-8',
    )
    const wordmarkSize = cn(
      'block w-auto min-w-0 shrink group-data-[collapsible=icon]:hidden!',
      size === 'md' ? 'h-12' : 'h-[var(--brand-lockup-height,2.5rem)]',
    )
    return (
      <>
        <BrandMark
          size={size}
          tone={tone}
          className={cn(markSize, swap && 'group-hover/menu-button:hidden')}
        />
        {swap ? (
          <BrandMark
            size={size}
            tone="surface"
            className={cn(markSize, 'hidden group-hover/menu-button:block')}
          />
        ) : null}
        <img
          src={tone === 'sidebar' ? brand.wordmark.sidebar : brand.wordmark.surface}
          alt={label}
          className={cn(wordmarkSize, swap && 'group-hover/menu-button:hidden')}
        />
        {swap ? (
          <img
            src={brand.wordmark.surface}
            alt=""
            aria-hidden="true"
            className={cn(wordmarkSize, 'hidden group-hover/menu-button:block')}
          />
        ) : null}
      </>
    )
  }
  const quiet =
    tone === 'sidebar'
      ? 'text-sidebar-foreground/85 group-hover/menu-button:text-sidebar-accent-foreground/85'
      : 'text-muted-foreground'
  // With an eyebrow the pair reads as a lockup ("EXAMPLE HOLDINGS / Studio"), so
  // the short name beats repeating the company inside the full name.
  const name = brand.eyebrow ? brand.shortName : brand.name
  return (
    <>
      <BrandMark size={size} tone={tone} />
      <span className="grid flex-1 text-left leading-tight">
        {brand.eyebrow ? (
          <span className={cn(quiet, 'truncate text-xs font-semibold')}>{brand.eyebrow}</span>
        ) : null}
        <span
          className={cn(
            'font-display truncate font-semibold',
            size === 'md' ? 'text-lg tracking-tight' : 'text-sm',
          )}
        >
          {name}
        </span>
        {showWorkspace && !brand.eyebrow ? (
          <span className={cn(quiet, 'truncate text-xs')}>Workspace</span>
        ) : null}
      </span>
    </>
  )
}
